export interface Volunteer {
  id: string;
  name: string;
  email: string;
  phone: string;
  volunteerStatus: VolunteerStatus;
  assignedManager: string;
  primaryArea?: string;
  secondaryArea?: string;
  firstServed?: string; // Date when they first served
  lastClinicServed?: string; // Date of last clinic
  avatarUrl?: string;
  createdAt?: string; // Date volunteer submitted their form (list item created)
  
  // New Business Unit fields
  businessUnit?: string; // Communications, Clinical, Operations, Finance, LEHR, Care, Technology
  buManager?: string; // Person/Group field for the manager
  assignedRole?: string; // Role within the business unit
  
  // Monthly availability
  januaryAvailability?: string;
  februaryAvailability?: string;
  marchAvailability?: string;
  aprilAvailability?: string;
  mayAvailability?: string;
  juneAvailability?: string;
  julyAvailability?: string;
  augustAvailability?: string;
  septemberAvailability?: string;
  octoberAvailability?: string;
  novemberAvailability?: string;
  decemberAvailability?: string;
  
  // Comments
  comments?: Comment[];
  
  // Legacy fields for backward compatibility
  title?: string; // Alias for name
  status?: VolunteerStatus; // Alias for volunteerStatus
  availability?: string;
  notes?: string;
  whatsapp?: string;
  
  // Compliance fields
  campus?: string;
  preferredContact?: string;
  bestTimeToContact?: string;
  volunteerRoleInterest?: string;
  additionalInformation?: string;
  volunteerTShirtSize?: string;
  addedToWhatsapp?: boolean | string;
  orientationCompleted?: boolean | string;
  backgroundCleared?: boolean | string;
  confidentiality?: boolean | string;
  volunteerBirthdate?: string;
  mailingAddress?: string;
  
  // Additional fields from SharePoint
  hipaaPrivacyTrainingCompleted?: string;
  hipaaSecurityTrainingCompleted?: string;
  scheduledForUpcomingClinic?: string;
  doYouHaveVolunteerTShirt?: string;
  orientationAssigned?: string;
  consentFormSent?: string;
  
  // Comment count for UI display
  commentCount?: number;
}

export enum VolunteerStatus {
  NEW_NEEDS_CONTACT = 'New Needs Contact',
  CONTACT_COMPLETE = 'Contact Complete',
  CONTACTED_NO_RESPONSE = 'Contacted, No Response',
  INACTIVE = 'Inactive'
}

export interface ClinicSchedule {
  id: string;
  title: string; // Clinic name/description
  date: string;
  startTime: string;
  endTime: string;
  volunteers: string[]; // Array of volunteer IDs
  notes?: string;
  status: ClinicStatus;
}

export enum ClinicStatus {
  SCHEDULED = 'Scheduled',
  CONFIRMED = 'Confirmed',
  CANCELLED = 'Cancelled',
  COMPLETED = 'Completed'
}

export interface ActivityLog {
  id: string;
  sender: string;
  mentionedUser: string;
  message: string;
  timestamp: string;
  type: ActivityType;
}

export enum ActivityType {
  MENTION = 'mention',
  COMMENT = 'comment',
  REMINDER = 'reminder',
  ASSIGNMENT = 'assignment',
  UPDATE = 'update'
}

export interface Comment {
  id: string;
  author: string;
  authorEmail?: string;
  content: string;
  timestamp: string;
  type: 'comment';
} 

// Department aliases to normalize legacy/business unit names to our canonical department keys
const DEPARTMENT_ALIASES: Record<string, string> = {
  'Communications': 'Marketing and Communications',
  'Marketing & Communications': 'Marketing and Communications',
  'Marketing and Communications': 'Marketing and Communications',
  'Finance': 'Finance & Development',
  'Finance and Development': 'Finance & Development',
  'Finance & Development': 'Finance & Development',
  'Legal & HR (LEHR)': 'LEHR',
  'Legal & HR': 'LEHR',
  'LEHR': 'LEHR',
  'Clinical': 'Clinical Operations',
  'Clinical Operations': 'Clinical Operations',
  'Clinical Operation': 'Clinical Operations',
  'Operations': 'Support Services',
  'Operational': 'Support Services',
  'Care': 'Support Services',
  'Support Services': 'Support Services',
  'Technology': 'Technology',
};

export const normalizeDepartment = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  return DEPARTMENT_ALIASES[raw] || raw;
};

// Canonical Department to Director mapping (formerly BU Leaders)
export const BUSINESS_UNIT_LEADERS: Record<string, string> = {
  'Marketing and Communications': 'Jarvis Boyd',
  'Clinical Operations': 'Melissa Curry',
  'Finance & Development': 'Leslie White',
  'LEHR': 'Dawn Stephens',
  'Support Services': 'Darryl Goss',
  'Technology': 'Marcus Norman',
  // Legacy mappings for backward compatibility
  'Clinical': 'Melissa Curry',
  'Operations': 'Darryl Goss',
  'Care': 'Darryl Goss',
};

// Business Unit to available roles mapping (canonical departments matching Org Chart)
export const BUSINESS_UNIT_ROLES: Record<string, string[]> = {
  'Marketing and Communications': ['Volunteer', 'Department Lead', 'Dept Lead', 'Board Member', 'Communications Manager', 'Community/Event Manager'],
  'Clinical Operations': ['Volunteer', 'Department Lead', 'Dept Lead', 'Board Member', 'Medical Director (Providers)', 'Patient Advocate Manager', 'Medical Services Manager', 'Quality Team Manager'],
  'Finance & Development': ['Volunteer', 'Department Lead', 'Dept Lead', 'Board Member', 'Finance Manager', 'Donor Development Manager', 'Procurement Manager', 'Tax/Audit Manager'],
  'LEHR': ['Volunteer', 'Department Lead', 'Dept Lead', 'Board Member', 'Compliance/Risk Manager', 'Evaluation Manager', 'HR Manager'],
  'Support Services': ['Volunteer', 'Department Lead', 'Dept Lead', 'Board Member', 'Administration Manager', 'Engagement Manager', 'Operations Manager', 'Language Services Manager', 'Spiritual Care Manager', 'Volunteer Care Manager'],
  'Technology': ['Volunteer', 'Department Lead', 'Dept Lead', 'Board Member', 'Systems Management Manager', 'Technical Support Manager', 'EHR Manager'],
  // Legacy mappings for backward compatibility
  'Clinical': ['Volunteer', 'Department Lead', 'Dept Lead', 'Board Member', 'Medical Director (Providers)', 'Patient Advocate Manager', 'Medical Services Manager', 'Quality Team Manager'],
  'Operations': ['Volunteer', 'Department Lead', 'Dept Lead', 'Board Member', 'Administration Manager', 'Engagement Manager', 'Operations Manager', 'Language Services Manager'],
  'Care': ['Volunteer', 'Department Lead', 'Dept Lead', 'Board Member', 'Spiritual Care Manager', 'Volunteer Care Manager']
};

// Role to BU Manager mapping (who holds each manager role)
export const ROLE_TO_BU_MANAGER: Record<string, string> = {
  'Communications Manager': 'Lydia Lawrence',
  'Medical Director (Providers)': 'Dr. Sarah Johnson',
  'Patient Advocate Manager': 'Maria Rodriguez',
  'Medical Services Manager': 'Dr. Michael Chen',
  'Administration Manager': 'Jennifer Smith',
  'Engagement Manager': 'David Wilson',
  'Operations Manager': 'Robert Brown',
  'Language Services Manager': 'Ana Garcia',
  'Finance Manager': 'Lisa Thompson',
  'Donor Development Manager': 'Mark Davis',
  'Procurement Manager': 'Karen White',
  'Tax/Audit Manager': 'Steven Lee',
  'Compliance/Risk Manager': 'Rachel Green',
  'Evaluation Manager': 'Thomas Anderson',
  'HR Manager': 'Patricia Martinez',
  'Spiritual Care Manager': 'Father James O\'Connor',
  'Volunteer Care Manager': 'Susan Clark',
  'Systems Management Manager': 'Lauren Bradley',
  'Technical Support Manager': 'Amanda Taylor',
  'EHR Manager': 'Daniel Rodriguez'
};

// Helper function to get BU Leader from Business Unit
// Uses hardcoded values as fallback - use useDepartmentDirectors hook for SharePoint data
export const getBULeader = (businessUnit?: string): string => {
  const key = normalizeDepartment(businessUnit);
  if (!key) return 'Not assigned';
  return BUSINESS_UNIT_LEADERS[key] || 'Not assigned';
};

// Type for department director
export interface DepartmentDirector {
  id: string;
  department: string;
  directorName: string;
  directorEmail?: string;
  assignedBy: string;
  assignedDate: string;
  isActive: boolean;
}

// Helper function to get available roles for a Business Unit
export const getAvailableRoles = (businessUnit?: string): string[] => {
  const key = normalizeDepartment(businessUnit);
  if (!key) return ['Volunteer'];
  return BUSINESS_UNIT_ROLES[key] || ['Volunteer'];
};

// Helper function to get BU Manager from assigned role
export const getBUManagerFromRole = (assignedRole?: string): string => {
  if (!assignedRole) return 'Not assigned';
  return ROLE_TO_BU_MANAGER[assignedRole] || 'Not assigned';
};

// Helper function to check if a volunteer is a manager (has a manager role)
export const isManagerRole = (assignedRole?: string): boolean => {
  if (!assignedRole) return false;
  return assignedRole.includes('Manager') || assignedRole.includes('Director');
};

// Business Unit to primary manager role mapping (canonical departments matching Org Chart)
export const BUSINESS_UNIT_PRIMARY_MANAGER_ROLE: Record<string, string> = {
  'Marketing and Communications': 'Communications Manager',
  'Clinical Operations': 'Medical Services Manager',
  'Finance & Development': 'Finance Manager',
  'LEHR': 'Compliance/Risk Manager',
  'Support Services': 'Operations Manager',
  'Technology': 'Systems Management Manager',
  // Legacy mappings for backward compatibility
  'Clinical': 'Medical Services Manager',
  'Operations': 'Operations Manager',
  'Care': 'Volunteer Care Manager'
};

// Helper function to get the BU Manager for a business unit (for regular volunteers)
export const getBUManagerOfBusinessUnit = (businessUnit?: string): string => {
  const key = normalizeDepartment(businessUnit);
  if (!key) return 'Not assigned';
  
  const primaryManagerRole = BUSINESS_UNIT_PRIMARY_MANAGER_ROLE[key];
  if (!primaryManagerRole) return 'Not assigned';
  
  return ROLE_TO_BU_MANAGER[primaryManagerRole] || 'Not assigned';
};

// Helper function to get the appropriate BU Manager display for a volunteer
export const getBUManagerDisplay = (volunteer: { businessUnit?: string; assignedRole?: string }): string => {
  if (!volunteer.assignedRole) return 'Not assigned';
  
  // If the volunteer is a manager, show their BU Leader
  if (isManagerRole(volunteer.assignedRole)) {
    return getBULeader(volunteer.businessUnit);
  }
  
  // If the volunteer is a regular volunteer, show their BU Manager based on business unit
  return getBUManagerOfBusinessUnit(volunteer.businessUnit);
}; 

// Centralized teams per department (canonical keys)
export const DEPARTMENT_TEAMS: Record<string, string[]> = {
  'Marketing and Communications': ['Marketing/Comm Team', 'Community Engagement Team'],
  'Clinical Operations': ['Registration Team', 'Providers Team', 'Patient Advocate Team', 'Medical Services Team', 'Quality Team'],
  'Finance & Development': ['Finance Team', 'Donor Development Team', 'Procurement Team', 'Tax/Audit Team'],
  'LEHR': ['Legal/Compliance Team', 'Human Resources Team'],
  'Support Services': ['Admin Team', 'SetUp / Church Liaison Team', 'Liaison/Greeter Team', 'Prayer Team'],
  'Technology': ['Systems Management Team', 'Technical Support Team', 'EHR Team'],
  // Legacy mappings for backward compatibility
  'Clinical': ['Registration Team', 'Providers Team', 'Patient Advocate Team', 'Medical Services Team', 'Quality Team'],
  'Operations': ['Admin Team', 'SetUp / Church Liaison Team', 'Liaison/Greeter Team', 'Prayer Team'],
  'Care': ['Admin Team', 'SetUp / Church Liaison Team', 'Liaison/Greeter Team', 'Prayer Team'],
};

// Team → manager role mapping per department (canonical keys)
export const TEAM_MANAGER_ROLE_BY_DEPARTMENT: Record<string, Record<string, string>> = {
  'Marketing and Communications': {
    'Marketing/Comm Team': 'Marketing/Comm Lead',
    'Community Engagement Team': 'Community Engagement Lead',
  },
  'Clinical Operations': {
    'Registration Team': 'Registration Lead',
    'Providers Team': 'Providers Lead',
    'Patient Advocate Team': 'Patient Advocate Lead',
    'Medical Services Team': 'Medical Services Lead',
    'Quality Team': 'Quality Lead',
  },
  'Finance & Development': {
    'Finance Team': 'Finance Lead',
    'Donor Development Team': 'Donor Development Lead',
    'Procurement Team': 'Procurement Lead',
    'Tax/Audit Team': 'Tax/Audit Lead',
  },
  'LEHR': {
    'Legal/Compliance Team': 'Legal/Compliance Lead',
    'Human Resources Team': 'Human Resource Lead',
  },
  'Support Services': {
    'Admin Team': 'Admin Lead',
    'SetUp / Church Liaison Team': 'SetUp / Church Liaison Lead',
    'Liaison/Greeter Team': 'Liaison/Greeter Lead',
    'Prayer Team': 'Prayer Lead',
  },
  'Technology': {
    'Systems Management Team': 'Systems Management Lead',
    'Technical Support Team': 'Technical Support Lead',
    'EHR Team': 'EHR Lead',
  },
  // Legacy mappings for backward compatibility
  'Clinical': {
    'Registration Team': 'Registration Lead',
    'Providers Team': 'Providers Lead',
    'Patient Advocate Team': 'Patient Advocate Lead',
    'Medical Services Team': 'Medical Services Lead',
    'Quality Team': 'Quality Lead',
  },
  'Operations': {
    'Admin Team': 'Admin Lead',
    'SetUp / Church Liaison Team': 'SetUp / Church Liaison Lead',
    'Liaison/Greeter Team': 'Liaison/Greeter Lead',
    'Prayer Team': 'Prayer Lead',
  },
  'Care': {
    'Admin Team': 'Admin Lead',
    'SetUp / Church Liaison Team': 'SetUp / Church Liaison Lead',
    'Liaison/Greeter Team': 'Liaison/Greeter Lead',
    'Prayer Team': 'Prayer Lead',
  },
};

export const getTeamsForDepartment = (businessUnit?: string): string[] => {
  const key = normalizeDepartment(businessUnit || '');
  if (!key) return [];
  return DEPARTMENT_TEAMS[key] || [];
};

export const getManagerRoleForTeam = (businessUnit?: string, team?: string): string | undefined => {
  const key = normalizeDepartment(businessUnit || '');
  if (!key || !team) return undefined;
  return TEAM_MANAGER_ROLE_BY_DEPARTMENT[key]?.[team];
};